import { AfterViewChecked, ChangeDetectorRef, Component, HostListener, Injector, OnInit, isDevMode } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { KeycloakService } from 'keycloak-angular';
import { Observable, debounceTime } from 'rxjs';
import { MOCK_MENU_USER } from 'src/mocks/users.mock';
import { SnackbarComponent } from './components/snackbar/snackbar.component';
import { AppConfig } from './constants/app-config';
import { IModules } from './model/http/menu.model';
import { IDataUserLogged } from './model/http/users.model';
import { SnackHorizontalPosition, SnackVerticalPosition } from './model/snackmsg/snackmsg.model';
import { DataService } from './services/data.service';
import { ThemeSwitcherService } from './services/theme-switcher.service';
import { UsersService } from './services/users.service';
import { UTILS } from './utils/methods/Utils';
import { SseResponseEnum } from './utils/sse/sse-responses';
import { MetaTagService } from './services/meta-tag.service';
import { SecurityService } from './services/security.service';


@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, AfterViewChecked {

  title = 'servicios-digitales-admin';
  now = new Date();
  isLoading!: Observable<boolean>;
  isBlocked = false;
  dataBlocked: any;

  //injectors
  dom: DomSanitizer;
  translate: TranslateService;
  themeSwitcher: ThemeSwitcherService;
  matIcon: MatIconRegistry;
  snackBar: MatSnackBar;

  dataUser: IDataUserLogged = {
    userName: '',
    userId: 0,
    shortName: '',
    lettersName: '',
    userEmail: '',
    fullName: '',
    rols: []
  }

  menuUser: Array<IModules> = [];
  userNotEnroll: boolean = false;

  globalLoading = false;
  keycloakReady = false;

  metaTagService: MetaTagService;

  constructor(
    public readonly dataService: DataService,
    private readonly injector: Injector,
    private readonly keycloackService: KeycloakService,
    private readonly usersService: UsersService,
    private readonly detectorRef: ChangeDetectorRef,
    private readonly securityService: SecurityService
  ) {
    this.dom = this.injector.get<DomSanitizer>(DomSanitizer);
    this.translate = this.injector.get<TranslateService>(TranslateService);
    this.themeSwitcher = this.injector.get<ThemeSwitcherService>(ThemeSwitcherService);
    this.matIcon = this.injector.get<MatIconRegistry>(MatIconRegistry);
    this.snackBar = this.injector.get<MatSnackBar>(MatSnackBar);
    this.metaTagService = this.injector.get<MetaTagService>(MetaTagService);

    this.translate.setDefaultLang('es');

    this.dataService.getGeneralNotificationMessage().subscribe((data) => {
      this.snackBar.openFromComponent(SnackbarComponent, {
        data,
        verticalPosition: SnackVerticalPosition.Top,
        horizontalPosition: SnackHorizontalPosition.Right,
        duration: AppConfig.generalMessageTimeout,
        panelClass: `type-${data.type}`
      });
    });

  }

  ngOnInit() {
    this.init();
  }

  async init() {

    this.initListeners();
    this.metaTagService.setMetaTags();
    await this.getUserKeycloak();

  }

  initListeners() {
    this.dataService.getIsLoading().pipe(debounceTime(0)).subscribe((flag: boolean) => this.globalLoading = flag);
    this.dataService.getBlockScreen().subscribe((resp: any) => {
      this.isBlocked = resp.show;
      this.dataBlocked = resp.data;
    });
    this.dataService.getSse$().subscribe(resp => this.evaluateSseCode(resp));
  }

  changeLanguage(lang: string): void {
    this.translate.use(lang);
  }

  changeTheme(theme: string): void {
    this.themeSwitcher.loadTheme(theme);
  }


  private evaluateSseCode(code: SseResponseEnum) {

    this.dataService.setNotificationSse(true);

    let cases: { [key in SseResponseEnum]?: any } = {
      [SseResponseEnum.TestResponse]: () => console.log(`TEST CODE ${code}`),
    }

    return cases[code]?.() ?? 'Operation not found';
  }

  async getUserKeycloak() {

    this.securityService.persistIdToken();
    const user = await this.keycloackService.loadUserProfile();

    let shortName = UTILS.formatNameUser(`${user.firstName} ${user.lastName}`);
    let lettersName = UTILS.formatLettersNameUser(shortName);
    let email = user?.email?.toLowerCase().trim() || '';

    this.dataUser = {
      userEmail: email,
      userName: email.split("@")[0],
      shortName: shortName,
      lettersName: lettersName,
      userId: 0,
      fullName: '',
      rols: []
    }

    if (this.isDevMode()) {
      this.menuUser = MOCK_MENU_USER.modules;
      this.dataUser.userId = MOCK_MENU_USER.user.id;
      this.dataUser.fullName = MOCK_MENU_USER.user.fullName;
      this.dataUser.rols = MOCK_MENU_USER.rols;
      this.dataService.dataUser = this.dataUser;
      this.dataService.setDataUser(this.dataUser);
    } else {
      this.getDataUser(email);
    }

    this.keycloakReady = true;


  }

  isDevMode() {
    return globalThis.location.hostname === 'localhost' && isDevMode();
  }

  getDataUser(email: string) {
    this.usersService.getMenuById(email).subscribe({
      next: (resp) => {
        this.menuUser = this.checkModulesMenu(resp.modules);
        this.dataUser.userId = resp.user.id;
        this.dataUser.fullName = resp.user.fullName;
        this.dataUser.rols = resp.rols;
        this.dataService.dataUser = this.dataUser;
        this.dataService.setDataUser(this.dataUser);

      },
      error: () => {
        this.userNotEnroll = true;
        this.dataService.setIsLoading(false);
      }
    })
  }

  checkModulesMenu(menu: IModules[]) {
    let filter = menu.map(module => {
      module.subModule = module.subModule.filter(sub => sub.services.length > 0)

      return module;
    });

    return filter;
  }

  ngAfterViewChecked(): void {
    this.detectorRef.detectChanges();
  }

  getCss() {

    return this.dataBlocked
      ? 'blockedAll'
      : 'notifications'

  }

  /************ EVENTS *****************/

  @HostListener('window:resize')
  onResize() {
    this.dataService.setScreenWidth$(window.innerWidth);
    this.dataService.setScreenHeight$(window.innerWidth);
  }


}
